"use client";

import {
  animate,
  motion,
  type MotionValue,
  useMotionValue,
  useTransform,
} from "motion/react";
import { useEffect, useId } from "react";

import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { cn } from "@/lib/utils";

import {
  LOGO_INNER_STROKE,
  LOGO_OUTER_STROKE,
  LOGO_PATHS,
  LOGO_VIEWBOX,
  type LogoPath,
  logoDrawRanges,
} from "./logo-paths";

// =============================================================================

export {
  LOGO_INNER_STROKE,
  LOGO_OUTER_STROKE,
  LOGO_PATH_LENGTHS,
  LOGO_PATHS,
  LOGO_VIEWBOX,
  logoDrawRanges,
} from "./logo-paths";
export type { LogoPath } from "./logo-paths";

const PATHS = LOGO_PATHS;
const OUTER_STROKE = LOGO_OUTER_STROKE;
const INNER_STROKE = LOGO_INNER_STROKE;
const DRAW_EASE = [0.65, 0, 0.35, 1] as const;
const DRAW_RANGES = logoDrawRanges();

type LogoProps = {
  className?: string;
  /** Accessible name, omit when the logo is decorative */
  title?: string;
  /** Draw the strokes on mount */
  animated?: boolean;
  /** Delay of the draw animation, in seconds */
  delay?: number;
  /** Duration of the whole drawing, in seconds */
  duration?: number;
  /** Disable the fading ends */
  solid?: boolean;
  /** Multiply the stroke widths (thicker footer watermark…) */
  weight?: number;
};

// =============================================================================

type DrawnPathProps = {
  d: string;
  stroke?: string;
  progress: MotionValue<number>;
  range: [number, number];
};

/** Path drawn over its share of the drawing progress */
function DrawnPath({ d, stroke, progress, range }: DrawnPathProps) {
  const pathLength = useTransform(progress, range, [0, 1], { clamp: true });
  // Hidden until the pen reaches it: its round cap would show as a dot
  const opacity = useTransform(progress, (value) => (value > range[0] ? 1 : 0));
  return <motion.path d={d} stroke={stroke} style={{ pathLength, opacity }} />;
}

// =============================================================================

export function Logo({
  className,
  title,
  animated = false,
  delay = 0,
  duration = 1.6,
  solid = false,
  weight = 1,
}: LogoProps) {
  const id = useId().replace(/:/g, "");
  const bowlGradient = `logo-bowl-${id}`;
  const innerGradient = `logo-inner-${id}`;
  const shouldReduceMotion = usePrefersReducedMotion();
  const progress = useMotionValue(0);

  // One pen: the "b" in one stroke, then the "c" (see logoDrawRanges)
  useEffect(() => {
    if (!animated) return;
    if (shouldReduceMotion) {
      progress.set(1);
      return;
    }
    const controls = animate(progress, 1, { duration, delay, ease: DRAW_EASE });
    return () => controls.stop();
  }, [animated, delay, duration, progress, shouldReduceMotion]);

  const path = (key: LogoPath, stroke?: string) =>
    animated ? (
      <DrawnPath
        d={PATHS[key]}
        stroke={stroke}
        progress={progress}
        range={DRAW_RANGES[key]}
      />
    ) : (
      <path d={PATHS[key]} stroke={stroke} />
    );

  return (
    <svg
      viewBox={LOGO_VIEWBOX}
      fill="none"
      strokeLinecap="round"
      className={cn("h-auto", className)}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {!solid && (
        <defs>
          <linearGradient
            id={bowlGradient}
            x1="256.426"
            y1="211.165"
            x2="351.413"
            y2="293.456"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="currentColor" stopOpacity="0" />
            <stop offset="1" stopColor="currentColor" />
          </linearGradient>
          <linearGradient
            id={innerGradient}
            x1="297.59"
            y1="347.798"
            x2="217.085"
            y2="347.881"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="currentColor" stopOpacity="0" />
            <stop offset="1" stopColor="currentColor" />
          </linearGradient>
        </defs>
      )}
      <g stroke="currentColor" strokeWidth={OUTER_STROKE * weight}>
        {path("stemAndBowl")}
        {path("bowlTop", solid ? undefined : `url(#${bowlGradient})`)}
      </g>
      <g stroke="currentColor" strokeWidth={INNER_STROKE * weight}>
        {path("innerTop")}
        {path("innerBottom", solid ? undefined : `url(#${innerGradient})`)}
      </g>
    </svg>
  );
}

// =============================================================================
